import * as React from "react";
import { useState } from "react";
import { OrderedSet } from "immutable";
import styled from "styled-components";
import { Field, Formik, Form } from "formik";
import ModalSubmit from "components/common/ModalSubmit";
import { Modal, Button } from "components/bootstrap";
import BootstrapModalWrapper from "components/bootstrap/BootstrapModalWrapper";
import ExportWidgetSelection from "views/components/export/ExportWidgetSelection";
import { MESSAGE_FIELD, SOURCE_FIELD, TIMESTAMP_FIELD } from "views/Constants";
import useSearchExecutionState from "views/hooks/useSearchExecutionState";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useCurrentUser from "hooks/useCurrentUser";
import useCurrentQuery from "views/logic/queries/useCurrentQuery";
import ExportSettings from "./ExportSettings";
import ExportStrategy from "./ExportStrategy";
import startDownload from "./startDownload";
const DEFAULT_FIELDS = OrderedSet([TIMESTAMP_FIELD, SOURCE_FIELD, MESSAGE_FIELD]);
const Content = styled.div`
  margin-left: 15px;
  margin-right: 15px;
`;
const _getInitialWidgetFields = (selectedWidget) => {
  if (selectedWidget.config.showMessageRow) {
    return OrderedSet(selectedWidget.config.fields).add(MESSAGE_FIELD).toOrderedSet();
  }
  return OrderedSet(selectedWidget.config.fields);
};
const _getInitialFields = (selectedWidget) => {
  const initialFields = selectedWidget ? _getInitialWidgetFields(selectedWidget) : DEFAULT_FIELDS;
  return initialFields.map((field) => ({ field })).toArray();
};
const ExportModal = ({ closeModal = () => {
}, view, directExportWidgetId = null }) => {
  const executionState = useSearchExecutionState();
  const sendTelemetry = useSendTelemetry("widget");
  const { state: viewStates } = view;
  const {
    shouldEnableDownload,
    title,
    initialWidget,
    shouldShowWidgetSelection,
    shouldAllowWidgetSelection,
    downloadFile
  } = ExportStrategy.createExportStrategy(view.type);
  const exportableWidgets = viewStates.map((state) => state.widgets.filter((widget) => widget.isExportable).toList()).toList().flatten(true);
  const currentUser = useCurrentUser();
  const currentQuery = useCurrentQuery();
  const [loading, setLoading] = useState(false);
  const initialSelectedWidget = initialWidget(exportableWidgets, directExportWidgetId);
  const initialSelectedFields = _getInitialFields(initialSelectedWidget);
  const singleWidgetDownload = !!directExportWidgetId;
  const _startDownload = ({ selectedWidget, selectedFields, limit, customSettings, format }) => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_WIDGET_EXPORT_DOWNLOADED, {});
    setLoading(true);
    return startDownload(
      format,
      downloadFile,
      view,
      executionState,
      selectedWidget,
      selectedFields,
      limit,
      customSettings,
      currentUser,
      currentQuery
    ).then(closeModal).finally(() => setLoading(false));
  };
  const initialValues = {
    selectedWidget: initialSelectedWidget,
    selectedFields: initialSelectedFields,
    limit: void 0,
    customSettings: {},
    format: "csv"
  };
  return /* @__PURE__ */ React.createElement(Formik, { onSubmit: _startDownload, initialValues }, ({ values: { selectedWidget, selectedFields }, setFieldValue }) => {
    const showWidgetSelection = shouldShowWidgetSelection(singleWidgetDownload, selectedWidget, exportableWidgets);
    const allowWidgetSelection = shouldAllowWidgetSelection(
      singleWidgetDownload,
      showWidgetSelection,
      exportableWidgets
    );
    const enableDownload = shouldEnableDownload(showWidgetSelection, selectedWidget, selectedFields, loading);
    const resetSelectedWidget = () => setFieldValue("selectedWidget", void 0);
    const setSelectedFields = (newFields) => setFieldValue("selectedFields", newFields);
    return /* @__PURE__ */ React.createElement(BootstrapModalWrapper, { showModal: true, onHide: closeModal }, /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(Modal.Header, null, /* @__PURE__ */ React.createElement(Modal.Title, null, title)), /* @__PURE__ */ React.createElement(Modal.Body, null, /* @__PURE__ */ React.createElement(Content, null, showWidgetSelection && /* @__PURE__ */ React.createElement(Field, { name: "selectedWidget" }, ({ field: { name, onChange } }) => {
      const onChangeSelectWidget = (widget) => {
        setSelectedFields(_getInitialFields(widget));
        return onChange({ target: { name, value: widget } });
      };
      return /* @__PURE__ */ React.createElement(
        ExportWidgetSelection,
        {
          selectWidget: onChangeSelectWidget,
          view,
          widgets: exportableWidgets.toList()
        }
      );
    }), !showWidgetSelection && /* @__PURE__ */ React.createElement(ExportSettings, { selectedWidget: initialSelectedWidget, view }))), /* @__PURE__ */ React.createElement(Modal.Footer, null, /* @__PURE__ */ React.createElement(
      ModalSubmit,
      {
        leftCol: allowWidgetSelection && /* @__PURE__ */ React.createElement(Button, { bsStyle: "link", onClick: resetSelectedWidget, className: "pull-left" }, "Select different message table"),
        onCancel: closeModal,
        disabledSubmit: !enableDownload,
        isSubmitting: loading,
        isAsyncSubmit: true,
        submitLoadingText: "Downloading...",
        submitIcon: "download",
        submitButtonText: "Start Download"
      }
    ))));
  });
};
export default ExportModal;
